'use client';

import ExportedImage from 'next-image-export-optimizer';
import { FC, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { gsap } from 'gsap';
import { Observer } from 'gsap/Observer';
import { debounce } from 'lodash';
import { Button, Tooltip } from '../../../../../libs/components/components';
import { AppRoute } from '../../../../../libs/enums/enums';
import { useScrollbarWidthVariable } from '../../../../../libs/hooks/hooks';

import 'swiper/css';
import './campaign-slider-hero.scss';

import Image1 from '../../../../../../public/images/Organo_WCW_Campaign_WomanCouch_Website_2560.webp';
import Image2 from '../../../../../../public/images/Organo_WCW_Campaign_WomanShop_Website_2_2560.webp';
import Image3 from '../../../../../../public/images/Organo_WCW_Campaign_ManWalkDog_Website_2_2560.webp';
import Image4 from '../../../../../../public/images/Organo_WCW_Campaign_ManDrOffice_Website_2560.webp';
import Image5 from '../../../../../../public/images/Organo_WCW_Campaign_AfrAmManBed_Website_1_2560.webp';
import Image6 from '../../../../../../public/images/Organo_WCW_Campaign_AfrAmManBook_Website_2560.webp';

import ScrollUpIcon from '../../../../../../public/svg/scroll-up.svg';
import ScrollDownIcon from '../../../../../../public/svg/scroll-down.svg';
import ArrowRight from '../../../../../../public/svg/arrow-right.svg';
import { cn } from '@shared/shared-utils';
import Link from 'next/link';

gsap.registerPlugin(Observer);

const swiperModules = [Mousewheel];

const imagePairs = [
  {
    left: Image5,
    right: Image6,
    leftAlt: 'Man resting at home; every day I waited, my wound got worse.',
    rightAlt: 'Man reading and seeking solutions; I wouldn’t let my wound win.',
    leftCaption: (
      <div className="w-full wcw-lg-min:max-w-[450px] ogn-2xl-min:max-w-[700px]">
        Every day I waited my <br /> wound got worse.
      </div>
    ),
    rightCaption: (
      <div className="wcw-lg-min:max-w-[600px] ogn-2xl-min:max-w-[735px]">
        I searched for solutions. <br /> I wouldn’t let my wound win.
      </div>
    ),
    imageLeftClassNames: 'max-w-[unset] w-full h-full',
    imageRightClassNames: 'max-w-[unset] w-full h-full',
  },
  {
    left: Image1,
    right: Image2,
    leftAlt: 'Woman on couch in pain; I stopped going shopping.',
    rightAlt: 'Woman shopping again; I took the next step.',
    leftCaption: (
      <div>
        My wound hurt so <br /> much I stopped <br /> going shopping.
      </div>
    ),
    rightCaption: (
      <div>
        I took the next step <br /> and took back my life.
      </div>
    ),
    imageLeftClassNames: 'max-w-[unset] w-full h-full',
    imageRightClassNames: 'max-w-[unset] w-full h-full',
  },
  {
    left: Image4,
    right: Image3,
    leftAlt: 'Man at the doctor’s office; waiting made my dog wait too.',
    rightAlt: 'Walking my dog again; I wouldn’t let my wound win.',
    leftCaption: (
      <>
        Waiting for my wound <br /> to heal made my <br /> dog wait too.
      </>
    ),
    rightCaption: (
      <div>
        I wouldn’t let my wound win. <br /> Now we’re both walking again.
      </div>
    ),
    imageLeftClassNames: 'max-w-[unset] w-full h-full',
    imageRightClassNames: 'max-w-[unset] w-full h-full',
  },
];

const ANIMATION_SPEED = 1200;

// Left column shows pairs in normal order; right shows reversed order
const leftImages = imagePairs.map((pair) => ({
  src: pair.left,
  alt: pair.leftAlt,
  caption: pair.leftCaption,
  classNames: pair.imageLeftClassNames,
}));
const rightImages = [...imagePairs].map((pair) => ({
  src: pair.right,
  alt: pair.rightAlt,
  caption: pair.rightCaption,
  classNames: pair.imageRightClassNames,
}));

const CampaignSliderHero: FC = () => {
  const leftSwiperRef = useRef<SwiperType | null>(null);
  const rightSwiperRef = useRef<SwiperType | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const observerRef = useRef<Observer | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLeftAnimating, setIsLeftAnimating] = useState(false);
  const [isRightAnimating, setIsRightAnimating] = useState(false);
  const [isHoveringImages, setIsHoveringImages] = useState(false);
  const [allowPageScroll, setAllowPageScroll] = useState(false);

  useScrollbarWidthVariable();

  const slideUp = useCallback(() => {
    if (isLeftAnimating || isRightAnimating || currentIndex === 0) return false;

    setIsLeftAnimating(true);

    const nextIndex = currentIndex - 1;

    setCurrentIndex(nextIndex);

    if (leftSwiperRef.current) {
      leftSwiperRef.current.slideTo(nextIndex, ANIMATION_SPEED);
    }
    if (rightSwiperRef.current) {
      rightSwiperRef.current.slideTo(nextIndex, ANIMATION_SPEED);
    }
    return true;
  }, [isLeftAnimating, isRightAnimating, currentIndex]);

  const slideDown = useCallback(() => {
    if (
      isLeftAnimating ||
      isRightAnimating ||
      currentIndex === imagePairs.length - 1
    )
      return false;

    setIsRightAnimating(true);

    const nextIndex = currentIndex + 1;

    setCurrentIndex(nextIndex);

    if (leftSwiperRef.current) {
      leftSwiperRef.current.slideTo(nextIndex, ANIMATION_SPEED);
    }
    if (rightSwiperRef.current) {
      rightSwiperRef.current.slideTo(nextIndex, ANIMATION_SPEED);
    }
    return true;
  }, [isRightAnimating, currentIndex, isLeftAnimating]);

  const handleMouseEnter = useCallback(() => {
    setIsHoveringImages(true);
    setAllowPageScroll(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHoveringImages(false);
    setAllowPageScroll(true);
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    observerRef.current = Observer.create({
      target: section,
      type: 'wheel',
      wheelSpeed: -1,
      tolerance: 10,
      onDown: debounce(() => {
        const isAnimating = isLeftAnimating || isRightAnimating;
        if (isAnimating) return;

        if (!isHoveringImages) {
          setAllowPageScroll(true);
          return;
        }

        const isLastSlide = currentIndex === imagePairs.length - 1;
        if (isLastSlide) {
          setAllowPageScroll(true);
          return;
        }

        setAllowPageScroll(false);
        slideDown();
      }, 50),
      onUp: debounce(() => {
        const isAnimating = isLeftAnimating || isRightAnimating;
        if (isAnimating) return;

        if (!isHoveringImages) {
          setAllowPageScroll(true);
          return;
        }

        const isFirstSlide = currentIndex === 0;
        if (isFirstSlide) {
          setAllowPageScroll(true);
          return;
        }

        setAllowPageScroll(false);
        slideUp();
      }, 50),
      preventDefault: !allowPageScroll,
      lockAxis: true,
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.kill();
      }
    };
  }, [
    isHoveringImages,
    currentIndex,
    isLeftAnimating,
    isRightAnimating,
    slideDown,
    slideUp,
    allowPageScroll,
  ]);

  // Slides with responsive object-fit strategy:
  // - Mobile: object-contain (avoid cropping important content)
  // - >= md: object-cover with tuned object-position
  const leftSlides = useMemo(
    () =>
      leftImages.map((image, index) => (
        <SwiperSlide key={`left-${index}`}>
          <div className="relative w-full h-full">
            <div className={cn('absolute inset-0', image.classNames)}>
              <ExportedImage
                key={`left-image-${index}`}
                src={image.src}
                alt={image.alt}
                className={cn(
                  'object-cover ogn-sm:min-h-[305px] ogn-sm:object-bottom md:object-cover md:object-top wcw-lg-min:object-[0_10%] max-w-[unset]',
                )}
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                fetchPriority={index === 0 ? 'high' : 'auto'}
                priority={index === 0}
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            </div>
            <div className="absolute inset-0 flex items-center justify-end 3xl:mr-[unset] bg-black bg-opacity-30">
              <div
                className="md:mr-20 lg:mr-16 wcw-lg-min:mr-36 3xl:mr-42 ogn-2xl-min:mr-60 text-white text-right md:text-left lg:text-right text-[26px] lg:justify-center wcw-lg-min:justify-end md:text-[24px] wcw-lg-min:text-[min(2vw,46px)] 3xl:text-[46px] 3xl:ml-32 ogn-2xl-min:text-[61px] font-roboto-condensed font-semibold px-5 lg:px-8"
                style={{
                  filter: 'drop-shadow(0 3px 20px rgba(0, 0, 0, 0.87))',
                }}
              >
                {image.caption}
              </div>
            </div>
          </div>
        </SwiperSlide>
      )),
    [],
  );

  const rightSlides = useMemo(
    () =>
      rightImages.map((image, index) => (
        <SwiperSlide key={`right-${index}`}>
          <div className="relative w-full h-full rotate-180">
            <div className={cn('absolute inset-0', image.classNames)}>
              <ExportedImage
                src={image.src}
                alt={image.alt}
                className={cn(
                  'object-cover ogn-sm:min-h-[305px] ogn-sm:object-bottom md:object-cover md:object-center max-w-[unset]',
                )}
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                fetchPriority={index === 0 ? 'high' : 'auto'}
                priority={index === 0}
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            </div>
            <div className="absolute inset-0 flex items-center justify-end md:justify-start wcw-lg-min:mr-[unset] bg-black bg-opacity-30">
              <div
                className="text-white text-right md:text-left text-[26px] md:text-[24px] md:ml-20 wcw-lg-min:text-[min(2vw,46px)] 3xl:text-[46px] font-roboto-condensed font-semibold px-5 lg:px-8 lg:ml-16 wcw-lg-min:ml-36 3xl:ml-42 ogn-2xl-min:text-[61px] ogn-2xl-min:ml-60"
                style={{
                  filter: 'drop-shadow(0 3px 20px rgba(0, 0, 0, 0.87))',
                }}
              >
                {image.caption}
              </div>
            </div>
          </div>
        </SwiperSlide>
      )),
    [],
  );

  const isAtFirst = currentIndex === 0 || isLeftAnimating || isRightAnimating;
  const isAtLast =
    currentIndex === imagePairs.length - 1 ||
    isLeftAnimating ||
    isRightAnimating;

  return (
    <section
      ref={sectionRef}
      className="flex flex-col md:flex-row w-screen-without-scrollbar lg:ml-[-360px] relative overflow-hidden ogn-sm:pointer-events-none md:cursor-grab md:active:cursor-grabbing"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label="Campaign hero slider"
    >
      <div className="relative w-full md:w-1/2 overflow-hidden">
        <div
          className={cn(
            'relative aspect-[239/180] md:aspect-[5/4] w-full',
            'ogn-sm:min-h-[307px] wcw-lg-min:max-h-[770px] wcw-xl-min:max-h-[1024px]',
          )}
        >
          <Swiper
            onSwiper={(swiper) => {
              leftSwiperRef.current = swiper;
            }}
            direction="vertical"
            modules={swiperModules}
            loop={false}
            allowTouchMove={false}
            mousewheel={false}
            initialSlide={0}
            className="absolute inset-0 h-full w-full ogn-sm:min-h-[307px] campaign-slider-hero"
            slidesPerView={1}
            spaceBetween={'0px'}
            onSlideChangeTransitionEnd={() => setIsLeftAnimating(false)}
            roundLengths={false}
          >
            {leftSlides}
          </Swiper>
        </div>
      </div>

      {/* RIGHT COLUMN */}
      <div className="relative w-full md:w-1/2 overflow-hidden">
        <div
          className={cn(
            'relative aspect-[239/180] md:aspect-[5/4] w-full',
            'ogn-sm:min-h-[307px] wcw-lg-min:max-h-[770px] wcw-xl-min:max-h-[1024px]',
          )}
        >
          <Swiper
            onSwiper={(swiper) => {
              rightSwiperRef.current = swiper;
            }}
            direction="vertical"
            modules={swiperModules}
            loop={false}
            allowTouchMove={false}
            mousewheel={false}
            initialSlide={0}
            className="absolute inset-0 h-full w-full ogn-sm:min-h-[307px] campaign-slider-hero right-slider rotate-180"
            slidesPerView={1}
            spaceBetween={'0px'}
            onSlideChangeTransitionEnd={() => setIsRightAnimating(false)}
            roundLengths={false}
          >
            {rightSlides}
          </Swiper>
        </div>
      </div>
      <div className="absolute top-1/2 left-5 md:left-1/2 md:-translate-x-1/2 -translate-y-1/2 flex flex-col z-10 pointer-events-none">
        <div
          className="flex flex-col uppercase w-[125px] h-[107px] md:w-[142px] md:h-[155px] wcw-lg-min:w-[275px] wcw-lg-min:h-[299px] 3xl:w-[276px] 3xl:h-[299px] ogn-2xl-min:w-[367px] ogn-2xl-min:h-[398px]"
          style={{
            filter: 'drop-shadow(0 3px 36px rgba(0, 0, 0, 0.84))',
          }}
        >
          <div className="bg-ogn-white text-ogn-blue text-center px-2 h-[82px] flex flex-col justify-center md:px-2 md:py-2 md:h-[93px] md:flex md:flex-col md:justify-center wcw-lg-min:px-8 wcw-lg-min:py-4 wcw-lg-min:h-[179px] wcw-lg-min:flex wcw-lg-min:flex-col wcw-lg-min:justify-center 3xl:px-8 3xl:py-4 3xl:h-[179px] 3xl:flex 3xl:flex-col 3xl:justify-center ogn-2xl-min:px-12 ogn-2xl-min:py-6 ogn-2xl-min:h-[239px] ogn-2xl-min:flex ogn-2xl-min:flex-col ogn-2xl-min:justify-center">
            <div className="text-[42px] leading-[42px] ogn-sm:mt-2 font-bold md:text-[42px] wcw-lg-min:font-bold md:leading-[45px] wcw-lg-min:text-[80px] wcw-lg-min:leading-[80px] ogn-2xl-min:text-[112px] ogn-2xl-min:leading-[112px] font-benton-sans-black">
              Life
            </div>
            <div className="text-[16px] font-bold md:text-[16px] wcw-lg-min:font-bold leading-[17px] wcw-lg-min:text-[30px] wcw-lg-min:leading-[32px] ogn-2xl-min:text-[42px] ogn-2xl-min:leading-[42px] font-benton-sans">
              IS WAITING
            </div>
          </div>
          <div className="flex flex-col gap-y-1 bg-ogn-blue text-ogn-white text-center py-1 h-[25px] justify-center md:py-1 md:h-[62px] md:justify-center md:gap-y-1 wcw-lg-min:py-4 wcw-lg-min:h-[120px] wcw-lg-min:justify-center 3xl:py-4 3xl:h-[120px] 3xl:justify-center 3xl:gap-y-4 ogn-2xl-min:py-6 ogn-2xl-min:h-[159px] ogn-2xl-min:justify-center ogn-2xl-min:gap-y-6">
            <span className="text-[8px] font-normal ogn-sm:leading-[10px] md:text-[10px] md:leading-[16px] wcw-lg-min:text-[20px] wcw-lg-min:leading-[32px] ogn-2xl-min:text-[26px] ogn-2xl-min:leading-[32px] font-benton-sans-black">
              YOUR WOUND CAN’T
            </span>
            <Tooltip className="justify-center" content="Start healing now">
              <Link
                href={AppRoute.HEALING_NAVIGATOR}
                className="hidden md:inline-flex items-center justify-center"
                aria-label="Start healing now"
              >
                <Button
                  variant="white"
                  className="flex items-center md:h-[20px] md:text-[8px] lg:text-[8px] md:px-2 md:gap-x-1 wcw-lg-min:h-[32px] wcw-lg-min:text-[14px] wcw-lg-min:px-4 wcw-lg-min:gap-x-2 3xl:h-[32px] 3xl:text-[14px] 3xl:px-4 3xl:gap-x-2 ogn-2xl-min:h-[42px] ogn-2xl-min:text-[18px] ogn-2xl-min:px-6 ogn-2xl-min:gap-x-3 gap-x-2 px-6 pointer-events-auto cursor-pointer rounded-full overflow-hidden transform-gpu will-change-transform [backface-visibility:hidden]"
                  aria-label="Start healing now"
                >
                  Start Healing Now{' '}
                  <ArrowRight
                    className="w-[12px] h-[12px] md:w-[8px] md:h-[8px] wcw-lg-min:w-[14px] wcw-lg-min:h-[14px] 3xl:w-[14px] 3xl:h-[14px] ogn-2xl-min:w-[18px] ogn-2xl-min:h-[18px]"
                    aria-hidden="true"
                    focusable="false"
                  />
                </Button>
              </Link>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="absolute bottom-[14px] lg:bottom-6 right-[24px] lg:right-12 z-10 pointer-events-auto">
        <Tooltip className="justify-center" content="Start healing now">
          <Link
            href={AppRoute.HEALING_NAVIGATOR}
            aria-label="Start healing now"
          >
            <Button
              variant="white"
              className="flex items-center gap-[6px] md:!text-[8px] md:hidden px-[14px] mb-[24px] pointer-events-auto cursor-pointer text-[16px] rounded-full overflow-hidden transform-gpu will-change-transform [backface-visibility:hidden]"
              aria-label="Start healing now"
            >
              Start Healing Now{' '}
              <ArrowRight
                className="w-[16px] h-[16px]"
                aria-hidden="true"
                focusable="false"
              />
            </Button>
          </Link>
        </Tooltip>

        <div className="flex justify-end">
          <Tooltip content="Previous slide">
            <button
              type="button"
              className={cn(
                'p-0 m-0 bg-transparent border-0',
                isAtFirst
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:opacity-70 transition-opacity',
              )}
              onClick={slideUp}
              aria-label="Previous slide"
              disabled={isAtFirst}
            >
              <ScrollUpIcon
                className="w-[43px] h-[27px]"
                aria-hidden="true"
                focusable="false"
              />
            </button>
          </Tooltip>

          <Tooltip content="Next slide">
            <button
              type="button"
              className={cn(
                'p-0 m-0 bg-transparent border-0',
                isAtLast
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:opacity-70 transition-opacity',
              )}
              onClick={slideDown}
              aria-label="Next slide"
              disabled={isAtLast}
            >
              <ScrollDownIcon
                className="w-[43px] h-[27px]"
                aria-hidden="true"
                focusable="false"
              />
            </button>
          </Tooltip>
        </div>
      </div>
    </section>
  );
};

export { CampaignSliderHero };
